import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import {
  fetchServices,
  fetchAvailability,
  createBooking,
} from "../lib/api";

const STEPS = ["Service", "Date & Time", "Your Details", "Confirmation"];

export default function Booking() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    fetchServices()
      .then((data) => {
        setServices(data);
        const presetId = params.get("service");
        if (presetId) {
          const found = data.find((s) => s.id === presetId);
          if (found) {
            setSelectedService(found);
            setStep(1);
          }
        }
      })
      .catch(() => toast.error("Could not load services"));
  }, [params]);

  const dateString = useMemo(
    () => (date ? format(date, "yyyy-MM-dd") : null),
    [date]
  );

  useEffect(() => {
    if (!dateString) return;
    setLoadingSlots(true);
    setTime(null);
    fetchAvailability(dateString)
      .then((data) => setSlots(data.slots))
      .catch(() => toast.error("Could not load availability"))
      .finally(() => setLoadingSlots(false));
  }, [dateString]);

  const canContinue = () => {
    if (step === 0) return !!selectedService;
    if (step === 1) return !!date && !!time;
    if (step === 2) return form.name.trim() && /.+@.+\..+/.test(form.email);
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: selectedService.id,
        service_name: selectedService.name,
        date: dateString,
        time,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      };
      const result = await createBooking(payload);
      setBookingResult(result);
      setStep(3);
      toast.success("Your session is booked", {
        description: `${selectedService.name} · ${format(
          date,
          "EEEE, MMMM d"
        )} at ${time}`,
      });
    } catch (e) {
      toast.error("Booking failed", {
        description: e?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="booking-page"
      className="pt-32 md:pt-36 px-6 md:px-12 lg:px-20 pb-24 min-h-screen"
    >
      <div className="max-w-5xl">
        <p className="font-eyebrow text-[11px] text-[#5C5C59]">
          Booking
        </p>
        <h1 className="font-serif-display text-5xl md:text-7xl text-[#1A1A1A] mt-4">
          Reserve your{" "}
          <em className="editorial-italic text-[#7B8871]">arrival.</em>
        </h1>
      </div>

      {/* Stepper */}
      <div
        data-testid="booking-stepper"
        className="mt-14 mb-14 flex items-center gap-4 md:gap-8 flex-wrap"
      >
        {STEPS.map((label, idx) => (
          <div key={label} className="flex items-center gap-3">
            <span
              className={`font-eyebrow text-[10px] w-7 h-7 flex items-center justify-center border ${
                idx <= step
                  ? "border-[#1A1A1A] text-[#1A1A1A]"
                  : "border-[#D8D4CC] text-[#B5BCAD]"
              }`}
            >
              0{idx + 1}
            </span>
            <span
              className={`font-eyebrow text-[10px] ${
                idx <= step ? "text-[#1A1A1A]" : "text-[#B5BCAD]"
              }`}
            >
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {step === 0 && (
            <div data-testid="step-service" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((s) => {
                const active = selectedService?.id === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`service-option-${s.id}`}
                    onClick={() => setSelectedService(s)}
                    className={`text-left border p-6 transition-all duration-300 ${
                      active
                        ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F7F5F2]"
                        : "border-[#D8D4CC] hover:border-[#7B8871] bg-[#FFFFFF]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <p
                        className={`font-eyebrow text-[10px] ${
                          active ? "text-[#B5BCAD]" : "text-[#5C5C59]"
                        }`}
                      >
                        {s.tagline}
                      </p>
                      <p
                        className={`font-eyebrow text-[10px] ${
                          active ? "text-[#B5BCAD]" : "text-[#7B8871]"
                        }`}
                      >
                        {s.duration}
                      </p>
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-3xl mt-3 leading-tight">
                      {s.name}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed mt-3 ${
                        active ? "text-[#D8D4CC]" : "text-[#5C5C59]"
                      }`}
                    >
                      {s.description}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-datetime" className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-4">
                  Choose a date
                </p>
                <div className="bg-[#FFFFFF] border border-[#D8D4CC] p-4 inline-block">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today || d.getDay() === 0;
                    }}
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
              <div>
                <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-4">
                  Available times
                </p>
                {!date && (
                  <p className="text-[#5C5C59] text-sm">
                    Select a date to see available windows.
                  </p>
                )}
                {date && loadingSlots && (
                  <p className="text-[#5C5C59] text-sm">Loading times…</p>
                )}
                {date && !loadingSlots && (
                  <div className="grid grid-cols-2 gap-3">
                    {slots.map((s) => {
                      const active = time === s.time;
                      return (
                        <button
                          key={s.time}
                          data-testid={`time-slot-${s.time.replace(/[: ]/g, "-")}`}
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          className={`px-4 py-3 font-eyebrow text-[11px] border transition-colors duration-300 ${
                            active
                              ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
                              : s.available
                              ? "bg-[#FFFFFF] border-[#D8D4CC] hover:border-[#1A1A1A] text-[#1A1A1A]"
                              : "border-[#EFECE5] text-[#B5BCAD] cursor-not-allowed"
                          }`}
                        >
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-details" className="max-w-2xl space-y-8">
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  Full name
                </label>
                <input
                  data-testid="input-name"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  className="input-underline"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  Email
                </label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className="input-underline"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  Phone (optional)
                </label>
                <input
                  data-testid="input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                  className="input-underline"
                  placeholder="+1 (___) ___-____"
                />
              </div>
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  What would you like us to know? (optional)
                </label>
                <textarea
                  data-testid="input-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) =>
                    setForm({ ...form, notes: e.target.value })
                  }
                  className="input-underline resize-none"
                  placeholder="What you'd like to explore in this session…"
                />
              </div>
            </div>
          )}

          {step === 3 && bookingResult && (
            <div
              data-testid="step-confirmation"
              className="border border-[#D8D4CC] bg-[#FFFFFF] p-10 max-w-2xl blur-in"
            >
              <div className="w-12 h-12 border border-[#7B8871] flex items-center justify-center mb-6">
                <Check strokeWidth={1.25} className="w-6 h-6 text-[#7B8871]" />
              </div>
              <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                Confirmation · #{bookingResult.id.slice(0, 8)}
              </p>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] mt-3">
                You're <em className="editorial-italic text-[#7B8871]">in.</em>
              </h2>
              <p className="text-[#5C5C59] text-sm leading-relaxed mt-4">
                A quiet reservation has been made for you. We'll hold this time
                gently. If anything changes, simply email{" "}
                <a
                  href="mailto:finkawholisticwellness@gmail.com"
                  className="underline decoration-[#7B8871]"
                >
                  finkawholisticwellness@gmail.com
                </a>
                .
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-[#D8D4CC] pt-6">
                <div>
                  <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                    Service
                  </p>
                  <p className="text-[#1A1A1A] mt-2">
                    {bookingResult.service_name}
                  </p>
                </div>
                <div>
                  <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                    Date
                  </p>
                  <p className="text-[#1A1A1A] mt-2">
                    {bookingResult.date} · {bookingResult.time}
                  </p>
                </div>
                <div>
                  <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                    Name
                  </p>
                  <p className="text-[#1A1A1A] mt-2">{bookingResult.name}</p>
                </div>
                <div>
                  <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                    Email
                  </p>
                  <p className="text-[#1A1A1A] mt-2">{bookingResult.email}</p>
                </div>
              </div>
              <div className="mt-8 flex gap-4">
                <Link
                  to="/affirmations"
                  data-testid="confirmation-cta-affirmations"
                  className="font-eyebrow text-[11px] px-6 py-3 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors"
                >
                  Read affirmations
                </Link>
                <Link
                  to="/"
                  data-testid="confirmation-cta-home"
                  className="font-eyebrow text-[11px] px-6 py-3 bg-[#1A1A1A] text-[#F7F5F2] hover:bg-[#7B8871] transition-colors"
                >
                  Back home
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar summary */}
        {step !== 3 && (
          <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="border border-[#D8D4CC] bg-[#FFFFFF] p-6">
              <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                Your reservation
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <p className="font-eyebrow text-[9px] text-[#B5BCAD]">
                    Service
                  </p>
                  <p className="font-serif-display text-xl text-[#1A1A1A] mt-1">
                    {selectedService?.name || "Not selected"}
                  </p>
                </div>
                <div>
                  <p className="font-eyebrow text-[9px] text-[#B5BCAD]">When</p>
                  <p className="text-[#1A1A1A] mt-1 text-sm">
                    {date ? format(date, "EEEE, MMM d") : "—"}{" "}
                    {time && <span>· {time}</span>}
                  </p>
                </div>
                {form.name && (
                  <div>
                    <p className="font-eyebrow text-[9px] text-[#B5BCAD]">
                      Guest
                    </p>
                    <p className="text-[#1A1A1A] mt-1 text-sm">{form.name}</p>
                  </div>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* Nav buttons */}
      {step < 3 && (
        <div className="mt-12 flex items-center justify-between">
          <button
            data-testid="booking-back-btn"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="group inline-flex items-center gap-2 font-eyebrow text-[11px] text-[#1A1A1A] disabled:text-[#B5BCAD] disabled:cursor-not-allowed"
          >
            <ArrowLeft
              strokeWidth={1.5}
              className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1"
            />
            Back
          </button>
          {step < 2 && (
            <button
              data-testid="booking-next-btn"
              disabled={!canContinue()}
              onClick={() => setStep((s) => s + 1)}
              className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue
              <ArrowRight
                strokeWidth={1.5}
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          )}
          {step === 2 && (
            <button
              data-testid="booking-confirm-btn"
              disabled={!canContinue() || submitting}
              onClick={handleSubmit}
              className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Reserving…" : "Confirm booking"}
              <ArrowRight
                strokeWidth={1.5}
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
